import { supabase } from '../client';

export interface B2BOptimization {
  id: string;
  org_id: string;
  application_id: string;
  candidate_id: string;
  job_id: string;
  b2c_optimization_id: string | null;
  optimized_resume_url: string | null;
  masked_resume_url: string | null;
  masking_template_id: string | null;
  alias_used: string | null;
  watermark_ref: string | null;
  ats_score_before: number | null;
  ats_score_after: number | null;
  /** MAT-010: full role-fit blob from optimize flow when persisted */
  fitment_analysis?: unknown;
  /** MAT-010: skills gap list from optimize flow when persisted */
  gap_analysis?: unknown;
  created_at: string;
}

function isUuid(s: string | null | undefined): boolean {
  return (
    !!s &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(s))
  );
}

export const optimizationService = {
  /**
   * MAT-020: When single-optimize is opened with `?applicationId=`, persist analyze blobs on
   * `ggj_b2b_optimizations` and copy score + summary JSON onto the application row.
   */
  async syncFromSingleOptimizeFlow(
    applicationId: string,
    payload: {
      b2c_optimization_id?: string | null;
      match_score?: number | null;
      initial_score?: number | null;
      fit_assessment?: unknown;
      skill_gaps?: unknown;
    }
  ): Promise<void> {
    const { data: app, error } = await supabase
      .from('ggj_b2b_applications')
      .select('org_id, candidate_id, job_id')
      .eq('id', applicationId)
      .single();
    if (error || !app) {
      console.warn('[optimizationService] syncFromSingleOptimizeFlow: application not found', error);
      return;
    }

    const b2cId =
      payload.b2c_optimization_id && isUuid(payload.b2c_optimization_id)
        ? payload.b2c_optimization_id
        : null;

    const fitment_analysis: Record<string, unknown> = {
      source: 'single_optimize',
      b2c_optimization_id: b2cId,
      fit_assessment: payload.fit_assessment ?? null,
      initial_score: payload.initial_score ?? null,
      match_score: payload.match_score ?? null,
    };
    const gap_analysis: Record<string, unknown> = {
      skill_gaps: payload.skill_gaps ?? null,
    };

    const { error: upErr } = await supabase.from('ggj_b2b_optimizations').upsert(
      {
        org_id: app.org_id,
        application_id: applicationId,
        candidate_id: app.candidate_id,
        job_id: app.job_id,
        b2c_optimization_id: b2cId,
        fitment_analysis,
        gap_analysis,
        ...(payload.match_score != null && !Number.isNaN(Number(payload.match_score))
          ? { ats_score_after: Number(payload.match_score) }
          : {}),
        ...(payload.initial_score != null && !Number.isNaN(Number(payload.initial_score))
          ? { ats_score_before: Number(payload.initial_score) }
          : {}),
      },
      { onConflict: 'application_id' }
    );
    if (upErr) {
      console.warn('[optimizationService] syncFromSingleOptimizeFlow: upsert failed', upErr);
      return;
    }

    const appPatch: Record<string, unknown> = { fitment_json: fitment_analysis };
    if (payload.match_score != null && !Number.isNaN(Number(payload.match_score))) {
      appPatch.fitment_score = Math.round(Number(payload.match_score));
    }
    const { error: appErr } = await supabase
      .from('ggj_b2b_applications')
      .update(appPatch)
      .eq('id', applicationId);
    if (appErr) console.warn('[optimizationService] syncFromSingleOptimizeFlow: application update failed', appErr);
  },

  async getByApplication(applicationId: string): Promise<B2BOptimization | null> {
    const { data } = await supabase
      .from('ggj_b2b_optimizations')
      .select('*')
      .eq('application_id', applicationId)
      .maybeSingle();
    return data;
  },

  async generateMaskedPdf(
    applicationId: string,
    maskingTemplateId?: string
  ): Promise<{ masked_resume_url: string; alias: string; watermark_ref: string }> {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ggj_b2b_generate-masked-pdf`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ application_id: applicationId, masking_template_id: maskingTemplateId }),
      }
    );
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data;
  },

  async getMaskedResumeUrl(path: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from('ggj_b2b-masked-resumes')
      .createSignedUrl(path, 3600);
    if (error) throw error;
    return data.signedUrl;
  },
};
